import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtEUR, fmtDate, INCOME, EXPENSE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function ImportPage() {
  const qc = useQueryClient();
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload"); // upload | mapping | review
  const [columns, setColumns] = useState([]);
  const [preview, setPreview] = useState([]);
  const [mapping, setMapping] = useState({ decimal: "auto", amount_mode: "single" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.get("/accounts").then((r) => r.data),
  });

  const upload = async (withMapping) => {
    if (!accountId) return toast.error("Escolha a conta de destino");
    if (!file) return toast.error("Escolha um ficheiro");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (withMapping) {
        const m = { ...mapping };
        if (mapping.amount_mode === "single") { delete m.debit_col; delete m.credit_col; }
        else { delete m.amount_col; }
        fd.append("mapping", JSON.stringify(m));
      }
      const { data } = await api.post("/import/parse", fd);
      if (data.needs_mapping) {
        setColumns(data.columns);
        setPreview(data.preview);
        setStep("mapping");
      } else {
        setRows(data.rows.map((r) => ({ ...r, selected: r.scope !== "empresa" })));
        setStep("review");
        if (data.source === "pdf") toast.success(`${data.rows.length} transações lidas do PDF`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const selected = rows.filter((r) => r.selected);
    if (selected.length === 0) return toast.error("Nenhuma transação selecionada");
    setBusy(true);
    try {
      const { data } = await api.post("/import/confirm", { account_id: accountId, rows: selected });
      toast.success(`${data.imported} importadas · ${data.skipped} duplicadas · ${data.excluded_empresa} empresa excluídas`);
      reset();
      qc.invalidateQueries();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep("upload"); setFile(null); setRows([]); setColumns([]); setPreview([]);
    setMapping({ decimal: "auto", amount_mode: "single" });
  };

  const colSelect = (key, label) => (
    <div>
      <Label>{label}</Label>
      <Select value={mapping[key] || ""} onValueChange={(v) => setMapping({ ...mapping, [key]: v })}>
        <SelectTrigger data-testid={`map-${key}`}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-5 max-w-4xl">
      <h1 className="font-heading text-2xl font-bold">Importar extrato</h1>

      {step === "upload" && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div>
            <Label>Conta de destino</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="import-account"><SelectValue placeholder="Escolha a conta" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ficheiro (PDF, Excel ou CSV)</Label>
            <label className="mt-1 flex items-center gap-3 border border-dashed border-border rounded-lg p-6 cursor-pointer hover:bg-secondary/50" data-testid="import-dropzone">
              <Upload className="text-muted-foreground" />
              <div className="text-sm">
                {file ? <span className="font-medium">{file.name}</span> : <span className="text-muted-foreground">Clique para escolher o ficheiro</span>}
              </div>
              <input type="file" accept=".pdf,.csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            </label>
            <p className="text-xs text-muted-foreground mt-2">PDFs são lidos automaticamente por IA. Reveja sempre antes de confirmar.</p>
          </div>
          <Button onClick={() => upload(false)} disabled={busy} className="rounded-full" data-testid="import-parse-btn">
            {busy ? "A processar…" : "Processar ficheiro"}
          </Button>
        </div>
      )}

      {step === "mapping" && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="font-semibold font-heading flex items-center gap-2"><FileText size={18} /> Mapeamento de colunas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {colSelect("date_col", "Coluna da data")}
            {colSelect("desc_col", "Coluna da descrição")}
            <div>
              <Label>Modo do valor</Label>
              <Select value={mapping.amount_mode} onValueChange={(v) => setMapping({ ...mapping, amount_mode: v })}>
                <SelectTrigger data-testid="map-amount-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Uma coluna (+entrada / −saída)</SelectItem>
                  <SelectItem value="split">Débito e crédito separados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Formato decimal</Label>
              <Select value={mapping.decimal} onValueChange={(v) => setMapping({ ...mapping, decimal: v })}>
                <SelectTrigger data-testid="map-decimal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automático</SelectItem>
                  <SelectItem value="comma">Vírgula (1.234,56)</SelectItem>
                  <SelectItem value="dot">Ponto (1,234.56)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mapping.amount_mode === "single" ? colSelect("amount_col", "Coluna do valor") : (
              <>
                {colSelect("debit_col", "Coluna de débito")}
                {colSelect("credit_col", "Coluna de crédito")}
              </>
            )}
          </div>

          <div className="overflow-x-auto no-scrollbar">
            <table className="text-xs w-full border border-border">
              <thead className="bg-secondary">
                <tr>{columns.map((c) => <th key={c} className="p-2 text-left font-medium">{c}</th>)}</tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    {columns.map((c) => <td key={c} className="p-2 truncate max-w-[150px]">{r[c]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={reset}>Voltar</Button>
            <Button onClick={() => upload(true)} disabled={busy} className="rounded-full" data-testid="map-confirm-btn">
              {busy ? "A processar…" : "Ver transações"}
            </Button>
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold font-heading">Rever ({rows.filter((r) => r.selected).length}/{rows.length} selecionadas)</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button onClick={confirm} disabled={busy} className="rounded-full" data-testid="import-confirm-btn">
                {busy ? "A importar…" : "Confirmar importação"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Linhas marcadas como <b>empresa</b> não entram nas finanças pessoais. Duplicados (mesma data + valor) são ignorados.</p>

          <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="review-list">
            {rows.map((r, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 ${r.scope === "empresa" ? "bg-[#C75D42]/5" : ""}`} data-testid={`review-row-${i}`}>
                <Checkbox checked={r.selected} onCheckedChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, selected: !!v } : x))} data-testid={`review-check-${i}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.description || "(sem descrição)"}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(r.date)} · {r.category || "sem categoria"}
                    {r.scope === "empresa" && <span className="text-[#C75D42] font-medium"> · empresa{r.scope_matched ? ` (${r.scope_matched})` : ""}</span>}
                  </div>
                </div>
                <div className="font-heading font-bold text-sm" style={{ color: r.type === "income" ? INCOME : EXPENSE }}>
                  {r.type === "income" ? "+" : "−"}{fmtEUR(r.amount)}
                </div>
                {r.selected ? <CheckCircle2 size={16} className="text-[#2E7D5C]" /> : <XCircle size={16} className="text-muted-foreground" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
